import React, { useState, useEffect, useRef } from "react";
import ApiService from "../../services/Api.service";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Alert from "../../components/Alert/Alert";
import Pagination from "../../components/Pagination/Pagination";
import StatusBadge from "../../utilities/StatusBadge";
import FilterBar from "../../components/FilterBar/FilterBar";
import Loader from "../../components/Loader/Loader";
import TableSubSection from "../../components/leads/TableSubSection";
import WebsocketService from "../../services/Websocket.service";
import TokenService from "../../services/Token.service";
import DispositionSubmitForm from "./DispositionSubmitForm";

export default function TransferCalls() {
    const [Loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [sections, setSections] = useState([]);
    const [postsPerPage, setPostsPerPage] = useState(20);
    const [headers, setHeaders] = useState([]);
    const [dropDownData, setDropDownData] = useState([]);
    const socketRef = useRef(null);
    const [wsConnected, setWsConnected] = useState(false);

    
    const [pagination, setPagination] = useState({
        page: 1,
        size: 20,
        total: 0,
        total_pages: 0,
    });
    const [alertValue, setAlertValue] = useState({
        show: false,
        message: "",
        variant: "success",
        position: "middle",
    });
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);
    const [campaignOptions, setCampaignOptions] = useState([]);
    const [dispositionOptions, setDispositionOptions] = useState([]);
    const [selectedDisposition, setSelectedDisposition] = useState({});
    const [selectedSubDisposition, setSelectedSubDisposition] = useState({});
    const [callbackDates, setCallbackDates] = useState({});
    const [remarks, setRemarks] = useState({});
    const [submittingDisposition, setSubmittingDisposition] = useState({});
    const [claimedCalls, setClaimedCalls] = useState({});
    const [openDispositionCallId, setOpenDispositionCallId] = useState(null);

    const validationSchema = yup.object().shape({
    });



    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            campaign_id: "",
            agents: [],
            products: [],
            search: "",
            date_from: "",
        }
    });
    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const [campaignRes, dispositionRes] = await Promise.all([
                ApiService.getCampaignOptions(),
                ApiService.getDispositionOptions()
            ]);
            const campaignData = campaignRes?.data?.data || [];
            const dispositionData = dispositionRes?.data?.data || [];

            setCampaignOptions(campaignData);
            setDispositionOptions(dispositionData);
        } catch (err) {
            console.error("Error loading dashboard data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const sendWsEvent = (event, data) => {
        if (socketRef.current?.readyState !== WebSocket.OPEN) return false;
        socketRef.current.send(JSON.stringify({ event, data }));
        return true;
    };

    const getCallId = (item) => item?.call_id || item?.id || item?.callId;

    const isDisconnectedCall = (item) => {
        const status = String(item?.status || "").toLowerCase();
        const transferStatus = String(item?.transfer_status || "").toLowerCase();
        const hasNoStatusFlag = !status || status === "--" || status === "null" || status === "undefined";
        return (
            hasNoStatusFlag ||
            ["disconnected", "ended", "completed", "hangup", "failed"].includes(status) ||
            ["disconnected", "ended", "completed", "hangup", "failed"].includes(transferStatus)
        );
    };

    const handleDispositionChange = (callId, value) => {
        setSelectedDisposition((prev) => ({ ...prev, [callId]: value }));
        setSelectedSubDisposition((prev) => ({ ...prev, [callId]: "" }));
        setCallbackDates((prev) => ({ ...prev, [callId]: "" }));
    };

    const handleSubDispositionChange = (callId, value) => {
        setSelectedSubDisposition((prev) => ({ ...prev, [callId]: value }));
        const normalized = String(value || "").toLowerCase().replace(/\s+/g, " ").trim();
        if (normalized !== "call back") {
            setCallbackDates((prev) => ({ ...prev, [callId]: "" }));
        }
    };

    const handleCallbackDateChange = (callId, value) => {
        setCallbackDates((prev) => ({ ...prev, [callId]: value }));
    };

    const handleRemarkChange = (callId, value) => {
        setRemarks((prev) => ({ ...prev, [callId]: value }));
    };

    const handleSubmitDisposition = async (callId) => {
        const row = tableData.find((item) => getCallId(item) === callId);
        const disposition = selectedDisposition[callId];
        const subDisposition = selectedSubDisposition[callId] || "";
        const callbackDate = callbackDates[callId] || "";
        const remark = remarks[callId] || "";
        if (!row) return;

        if (!disposition) {
            setAlertValue({
                show: true,
                message: "Please select disposition",
                variant: "danger",
                position: "middle",
            });
            return;
        }

        try {
            setSubmittingDisposition((prev) => ({ ...prev, [callId]: true }));
            const payload = {
                campaign_id: row?.campaign_id || row?.campaign || "",
                disposition: disposition,
                sub_disposition: subDisposition,
                callback_date: callbackDate || null,
                remark: remark || "",
                call_id: callId
            };
            await ApiService.updateCallEndDisposition(payload);

            setTableData((prev) =>
                prev.map((item) =>
                    getCallId(item) === callId
                        ? {
                            ...item,
                            disposition,
                            sub_disposition: subDisposition,
                            callback_date: callbackDate || null,
                            remark: remark || "",
                            status: "ended"
                          }
                        : item
                )
            );

            setAlertValue({
                show: true,
                message: "Disposition submitted successfully",
                variant: "success",
                position: "middle",
            });
            setOpenDispositionCallId(null);
        } catch (error) {
            setAlertValue({
                show: true,
                message: error?.response?.data?.detail || "Failed to submit disposition",
                variant: "danger",
                position: "middle",
            });
        } finally {
            setSubmittingDisposition((prev) => ({ ...prev, [callId]: false }));
        }
    };
    useEffect(() => {
        const ws = new WebSocket(WebsocketService.getTransferCalls());
        socketRef.current = ws;

        ws.onopen = () => {
            setWsConnected(true);
            const token = TokenService.getLocalAccessToken();
            const data = {
                access_token: token
            }
            sendWsEvent("connect", data);
        };

        ws.onmessage = (messageEvent) => {
            let payload;
            try {
                payload = JSON.parse(messageEvent.data);
            } catch (error) {
                console.error("Invalid websocket payload:", messageEvent.data);
                return;
            }

            const event = payload?.event;
            const data = payload?.data;

            switch (event) {
                case "headers": {
                    if (Array.isArray(data) && data.length) {
                        setHeaders(data);
                    }
                    break;
                }
                case "transfer": {
                    if (data) {
                        setTableData((prev) => [data, ...prev]);
                        setPagination((prev) => ({ ...prev, total: prev.total + 1 }));
                    }
                    break;
                }
                case "claimed": {
                    const callId = data?.call_id;
                    if (callId) {
                        // Remove row from table
                        setTableData((prev) =>
                            prev.filter((item) => getCallId(item) !== callId)
                        );

                        // Update pagination total
                        setPagination((prev) => ({
                            ...prev,
                            total: Math.max(prev.total - 1, 0),
                        }));
                    }
                    break;
                }
                case "error": {
                    break;
                }
                case "message": {
                    if (data?.text) {
                    }
                    break;
                }
                default:
                    break;
            }
        };

        ws.onerror = (err) => {
            console.error("Live transfer ws error:", err);
            setWsConnected(false);
        };

        ws.onclose = () => {
            setWsConnected(false);
        };

        return () => ws.close();
    }, []);



    const fetchDropdownDetail = async () => {
        try {
            const subprocessRes = await ApiService.getSubprocesses();
            if (subprocessRes.status === 200) setDropDownData(subprocessRes.data)
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchDropdownDetail();
    }, []);

    const handelSubmit = async (data) => {
        const payload = Object.fromEntries(
            Object.entries(data).filter(([_, v]) => {
                if (Array.isArray(v)) return v.length > 0;
                return v !== "" && v !== null && v !== undefined;
            })
        );
    };


    const handleClearFilters = () => {
        reset({ campaign_id: "", agents: [], products: [], search: "", date_from: "" });
    };

    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPostsPerPage(newSize);
        setSelectedRowIndex(null);
    };

    const mistableTh = {
        backgroundColor: "transparent",
        color: "rgba(43, 58, 103, 1)",
        fontFamily: "Nunito",
        fontSize: "12px",
        fontWeight: 700,
        lineHeight: "16px",
        letterSpacing: "0.1em",
        textAlign: "center",
    };

    const tableTdStyles = {
        backgroundColor: "#F7F7F7",
        fontFamily: "Nunito",
        fontSize: "12px",
        fontWeight: "700",
        lineHeight: "16px",
        letterSpacing: "0em",
        textAlign: "center",
        color: "#232122",
    };

    return (
        <> {alertValue.show && (
            <Alert
                variant={alertValue.variant}
                message={alertValue.message}
                dismissible={true}
                timeout={2500}
                position={alertValue.position}
                onClose={() => setAlertValue({ show: false, message: "", variant: "" })}
            />
        )}

            <div className="main-contanier">
                <FilterBar
                    control={control}
                    handleSubmit={handleSubmit}
                    onSubmit={handelSubmit}
                    onClearFilters={handleClearFilters}
                    filters={[]}
                    title={"All Transfer Calls"}
                    totalCount={pagination.total || 0}
                    tableId="table-id"
                    isDownload={false}
                />

                <div className="table-container dailymis-table" style={{ height: "calc(100vh - 210px)", position: "relative" }}>
                    {Loading && <Loader fullScreen={false} message="Loading leads..." />}
                    <table id="table-id" className="custom-table-container">
                        <thead
                            className="table-head"
                            style={{ position: "sticky", top: "0", zIndex: "1" }}
                        >
                            <tr>
                                <th style={mistableTh}>S.No</th>
                                {headers.map((header, i) => (
                                    <th key={i} style={mistableTh}>
                                        {header.label}
                                    </th>
                                ))}
                                <th style={mistableTh}> Actions </th>
                            </tr>
                        </thead>

                        <tbody>
                            {tableData.length > 0 ? (
                                tableData.map((item, index) => (
                                    <React.Fragment key={index}>
                                        <tr
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                                setSelectedRowIndex(selectedRowIndex === index ? null : index)
                                            }
                                        >
                                            <td
                                                style={{
                                                    ...tableTdStyles,
                                                    backgroundColor:
                                                        selectedRowIndex === index ? "#f9faff" : "transparent",
                                                }}
                                            >
                                                {index + 1}
                                            </td>
                                            {headers.map((header, i) => (
                                                <td
                                                    key={i}
                                                    style={{
                                                        ...tableTdStyles,
                                                        backgroundColor:
                                                            selectedRowIndex === index ? "#f9faff" : "transparent",
                                                    }}
                                                    title={item[header.value] ?? "--"}
                                                >
                                                    {header.value === "status" ? (
                                                        <StatusBadge status={item.status} />
                                                    ) : header.value === "disposition" ? (
                                                        <StatusBadge status={item.disposition} />
                                                    ) : (
                                                        item[header.value] ?? "--"
                                                    )}
                                                </td>
                                            ))}
                                            <td
                                                style={{
                                                    ...tableTdStyles,
                                                    backgroundColor:
                                                        selectedRowIndex === index ? "#f9faff" : "transparent",
                                                }}
                                            >
                                                {claimedCalls[item.call_id] ? (
                                                    <span style={{ color: "green", fontWeight: "bold" }}>Claimed</span>
                                                ) : (
                                                    <button
                                                        className="popup-add-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const callId = getCallId(item);
                                                            sendWsEvent("claim", {
                                                                call_id: callId,
                                                                agent_id: TokenService.getLocalAccessToken(),
                                                                campaign_id: item.campaign_id,
                                                                company_id: item.company_id
                                                            });
                                                            setClaimedCalls((prev) => ({ ...prev, [callId]: true }));
                                                        }}
                                                        disabled={wsConnected ? false : true}
                                                        style={{ padding: "5px 10px", fontSize: "12px" }}
                                                    >
                                                        Claim
                                                    </button>
                                                )}
                                            </td>
                                        </tr>

                                        {/* Expanded Sub Section */}
                                        {selectedRowIndex === index && (
                                            <tr>
                                                <td colSpan={headers.length + 2}>
                                                    <TableSubSection
                                                        data={item}
                                                        sections={sections}
                                                        onOpenCloseDisposition={(callId) => setOpenDispositionCallId(callId)}
                                                        onBackDisposition={() => setOpenDispositionCallId(null)}
                                                        isDispositionOpen={openDispositionCallId === getCallId(item)}
                                                        dispositionForm={
                                                            openDispositionCallId === getCallId(item) ? (
                                                                <DispositionSubmitForm
                                                                    callId={getCallId(item)}
                                                                    campaignId={item?.campaign_id || item?.campaign || ""}
                                                                    isDisconnected={isDisconnectedCall(item)}
                                                                    options={dispositionOptions}
                                                                    value={selectedDisposition[getCallId(item)] || ""}
                                                                    subDisposition={selectedSubDisposition[getCallId(item)] || ""}
                                                                    callbackDate={callbackDates[getCallId(item)] || ""}
                                                                    remark={remarks[getCallId(item)] || ""}
                                                                    submitting={Boolean(submittingDisposition[getCallId(item)])}
                                                                    onChange={handleDispositionChange}
                                                                    onSubDispositionChange={handleSubDispositionChange}
                                                                    onCallbackDateChange={handleCallbackDateChange}
                                                                    onRemarkChange={handleRemarkChange}
                                                                    onSubmit={handleSubmitDisposition}
                                                                />
                                                            ) : null
                                                        }
                                                        isCallEndButton={Boolean(claimedCalls[getCallId(item)])}
                                                    />

                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={headers.length + 2} style={tableTdStyles}>
                                        No data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

        </>
    );
}
